import Fuse from 'fuse.js';
import {
  normalizeText,
  preprocessForMatching,
  areSynonymous,
  getTermVariations
} from './synonyms';

/**
 * Default system-wide matching weights for CDMO scoring
 * These are used when users haven't customized their preferences
 * Column names match the database table structure
 */
export { DEFAULT_TIER2_SLIDER_MULTIPLIER } from '../constants/scoring';

export const defaultMatchingWeights = {
  company_name: 3,                    // Company name search (fixed Tier 2; not slider)
  primary_capabilities: 3,            // Used only if Tier 1 gate scoring; normally 0 via getTier2ScoringWeights
  detailed_capabilities: 5,         // Same
  engagement_scales: 3,               // Development stage (Tier 2 slider; neutral default)
  preferred_project_types: 3,         // Support services (Tier 2 slider)
  typical_batch_sizes: 3,             // Batch size (Tier 2 slider)
  target_client_types: 1,             // Partner size match — fixed default (slider removed)
  gmp_certifications: 3,              // Tier 1 gate; scoring weight forced to 0 in Tier 2
  speed_to_start: 3,                  // Tier 2 slider
  description: 1,                    // Additional details — fixed (slider removed)
  facility_locations_details: 1,      // Fixed (slider removed)
  budget: 3,                          // Tier 2 slider
  operating_regions: 3               // Geography — Tier 2 slider
};

/**
 * Weight keys exposed as Tier 2 user sliders (after Tier 1 gatekeepers).
 * Order matches the matching sidebar.
 */
export const TIER2_CUSTOMIZABLE_WEIGHT_KEYS = [
  'typical_batch_sizes',
  'engagement_scales',
  'speed_to_start',
  'budget',
  'operating_regions',
  'preferred_project_types'
] as const;

export type Tier2CustomizableWeightKey = (typeof TIER2_CUSTOMIZABLE_WEIGHT_KEYS)[number];

/**
 * Interface for user-customizable matching weights
 */
export interface MatchingWeights {
  company_name?: number;
  primary_capabilities?: number;
  detailed_capabilities?: number;
  engagement_scales?: number;
  preferred_project_types?: number;
  typical_batch_sizes?: number;
  target_client_types?: number;
  gmp_certifications?: number;
  speed_to_start?: number;
  description?: number;
  facility_locations_details?: number; // Optional field for location-based matching
  budget?: number;                     // Budget preference weight
  operating_regions?: number;                  // Geography/location preference weight
}

/**
 * Get effective weights by merging user preferences with defaults
 */
export const getEffectiveWeights = (userWeights?: MatchingWeights): typeof defaultMatchingWeights => {
  return {
    ...defaultMatchingWeights,
    ...userWeights
  };
};

/**
 * Weights used for Tier 2 ranking after Tier 1 gatekeeper filters.
 * Product type, detailed services, and certifications contribute only in Tier 1 (binary); their scoring weights are zero here.
 * Only {@link TIER2_CUSTOMIZABLE_WEIGHT_KEYS} are merged from user custom preferences.
 * Search, partner fit, and “additional details” use fixed system defaults (sliders removed).
 *
 * @param userWeights - Optional overrides from “Customize My Priorities” (Tier 2 sliders only)
 * @returns Full weight map for `calculateMatchScore`-style logic
 */
export const getTier2ScoringWeights = (
  userWeights?: Partial<MatchingWeights>
): typeof defaultMatchingWeights => {
  const out: typeof defaultMatchingWeights = { ...defaultMatchingWeights };

  out.primary_capabilities = 0;
  out.detailed_capabilities = 0;
  out.gmp_certifications = 0;

  out.company_name = defaultMatchingWeights.company_name;
  out.description = defaultMatchingWeights.description;
  out.facility_locations_details = defaultMatchingWeights.facility_locations_details;
  out.target_client_types = defaultMatchingWeights.target_client_types;

  if (userWeights) {
    for (const key of TIER2_CUSTOMIZABLE_WEIGHT_KEYS) {
      const v = userWeights[key];
      if (v !== undefined && v !== null) {
        out[key] = v;
      }
    }
  }

  return out;
};


/**
 * Configuration for different types of fuzzy matching
 */
const FUZZY_SEARCH_CONFIGS = {
  // For company names - more flexible for partial matches like "exx" -> "growexx"
  companyName: {
    threshold: 0.5, // More flexible for partial matching
    distance: 100,
    includeScore: true,
    ignoreLocation: true,
    ignoreFieldNorm: false,
    includeMatches: true,
    keys: ['text']
  },

  // For capabilities and services - more flexible
  capabilities: {
    threshold: 0.3, // Higher = more flexible
    distance: 100,
    includeScore: true,
    ignoreLocation: true,
    ignoreFieldNorm: false,
    includeMatches: true,
    keys: ['text']
  },

  // For certifications - moderate flexibility
  certifications: {
    threshold: 0.3,
    distance: 100,
    includeScore: true,
    ignoreLocation: true,
    ignoreFieldNorm: false,
    includeMatches: true,
    keys: ['text']
  },

  // For general text search - very flexible
  general: {
    threshold: 0.5, // Very flexible for general search
    distance: 100,
    includeScore: true,
    ignoreLocation: true,
    ignoreFieldNorm: false,
    includeMatches: true,
    keys: ['text']
  },

  // For partial search - optimized for substring matching like "exx" -> "growexx"
  partialSearch: {
    threshold: 0.5, // Very flexible for partial matches
    distance: 100,
    includeScore: true,
    ignoreLocation: true,
    ignoreFieldNorm: false,
    includeMatches: true,
    keys: ['text']
  }
};

/**
 * Enhanced fuzzy matcher that combines Fuse.js with synonym support
 */
export class EnhancedFuzzyMatcher {
  private fuseInstances: Map<string, Fuse<{ text: string }>> = new Map();

  /**
   * Create or get a Fuse instance for a specific type
   */
  private getFuseInstance(type: keyof typeof FUZZY_SEARCH_CONFIGS, data: string[]): Fuse<{ text: string }> {
    const cacheKey = `${type}-${data.length}`;

    if (!this.fuseInstances.has(cacheKey)) {
      const fuseData = data.map(text => ({ text: preprocessForMatching(text) }));
      const config = FUZZY_SEARCH_CONFIGS[type];
      this.fuseInstances.set(cacheKey, new Fuse(fuseData, config));
    }

    return this.fuseInstances.get(cacheKey)!;
  }

  /**
 * Enhanced matching that combines exact, synonym, fuzzy, and partial matching
 */
  public findMatches(
    query: string,
    candidates: string[],
    type: keyof typeof FUZZY_SEARCH_CONFIGS = 'general'
  ): MatchResult[] {
    if (!query || !candidates.length) return [];

    const processedQuery = preprocessForMatching(query);
    const queryVariations = getTermVariations(query);

    const results: MatchResult[] = [];

    candidates.forEach((candidate, originalIndex) => {
      const processedCandidate = preprocessForMatching(candidate);

      let score = 0;
      let matchType: 'exact' | 'synonym' | 'fuzzy' | 'partial' = 'fuzzy';

      // 1. Exact match (highest priority)
      if (processedQuery === processedCandidate) {
        score = 1.0;
        matchType = 'exact';
      }
      // 2. Substring/Partial match (very high priority for cases like "exx" -> "growexx")
      else if (this.isPartialMatch(query, candidate)) {
        const partialScore = this.calculatePartialScore(query, candidate);
        if (partialScore > 0.3) { // Minimum threshold for partial matches
          score = partialScore;
          matchType = 'partial';
        }
      }
      // 3. Synonym match (high priority)
      else if (areSynonymous(query, candidate)) {
        score = 0.95;
        matchType = 'synonym';
      }
      // 4. Check if any query variations match candidate variations
      else {
        const candidateVariations = getTermVariations(candidate);
        let foundSynonymMatch = false;

        for (const queryVar of queryVariations) {
          for (const candVar of candidateVariations) {
            if (normalizeText(queryVar) === normalizeText(candVar)) {
              score = 0.9;
              matchType = 'synonym';
              foundSynonymMatch = true;
              break;
            }
          }
          if (foundSynonymMatch) break;
        }

        // 5. Fuzzy match (lowest priority)
        if (!foundSynonymMatch) {
          const fuse = this.getFuseInstance(type, [candidate]);
          const fuseResults = fuse.search(processedQuery);

          if (fuseResults.length > 0) {
            // Fuse.js returns scores where 0 = perfect match, 1 = no match
            // Convert to our scale where 1 = perfect match, 0 = no match
            const fuseScore = 1 - (fuseResults[0].score || 0);

            // Only include if above threshold
            if (fuseScore > (1 - FUZZY_SEARCH_CONFIGS[type].threshold)) {
              score = fuseScore * 0.8; // Cap fuzzy matches at 0.8
              matchType = 'fuzzy';
            }
          }
        }
      }

      if (score > 0) {
        results.push({
          text: candidate,
          score,
          matchType,
          originalIndex
        });
      }
    });

    // Sort by score (highest first) then by match type priority
    return results.sort((a, b) => {
      if (a.score !== b.score) return b.score - a.score;

      const typePriority = { exact: 4, partial: 3, synonym: 2, fuzzy: 1 };
      return typePriority[b.matchType] - typePriority[a.matchType];
    });
  }

  /**
   * Check if query is a partial match of candidate (like "exx" in "growexx")
   */
  private isPartialMatch(query: string, candidate: string): boolean {
    const normalizedQuery = normalizeText(query);
    const normalizedCandidate = normalizeText(candidate);

    // Check if query is a substring of candidate (e.g., "exx" in "growexx")
    if (normalizedCandidate.includes(normalizedQuery)) {
      return true;
    }

    // Check reverse case, useful for when a user types a full name and it matches a shorter DB entry
    if (normalizedQuery.includes(normalizedCandidate)) {
      return true;
    }
    const queryWords = (normalizedQuery ?? '')
      .split(' ')
      .filter(word => word.length > 1);

    const candidateWords = (normalizedCandidate ?? '')
      .split(' ')
      .filter(word => word.length > 1);


    for (const qw of queryWords) {
      for (const cw of candidateWords) {
        if (cw.includes(qw) || qw.includes(cw)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Calculate score for partial matches
   */
  private calculatePartialScore(query: string, candidate: string): number {
    const normalizedQuery = normalizeText(query);
    const normalizedCandidate = normalizeText(candidate);

    // Direct substring match
    if (normalizedCandidate.includes(normalizedQuery)) {
      // Score based on how much of the candidate is covered by the query
      const coverage = normalizedQuery.length / normalizedCandidate.length;
      // Boost score for shorter queries that match longer strings
      const lengthBonus = Math.min(normalizedQuery.length / 10, 0.3);
      return Math.min(0.7 + coverage + lengthBonus, 0.95);
    }

    // Reverse case
    if (normalizedQuery.includes(normalizedCandidate)) {
      const coverage = normalizedCandidate.length / normalizedQuery.length;
      return Math.min(0.6 + coverage, 0.9);
    }

    // Word-level partial matching
    const queryWords = normalizedQuery.split(' ').filter(word => word.length > 1);
    const candidateWords = normalizedCandidate.split(' ').filter(word => word.length > 1);

    if (queryWords.length > 0) {
      let totalScore = 0;
      let wordMatches = 0;

      queryWords.forEach(queryWord => {
        candidateWords.forEach(candidateWord => {
          if (candidateWord.includes(queryWord)) {
            const wordCoverage = queryWord.length / candidateWord.length;
            totalScore += 0.4 + (wordCoverage * 0.3);
            wordMatches++;
          } else if (queryWord.includes(candidateWord)) {
            const wordCoverage = candidateWord.length / queryWord.length;
            totalScore += 0.3 + (wordCoverage * 0.2);
            wordMatches++;
          }
        });
      });

      if (wordMatches > 0) {
        const averageScore = totalScore / wordMatches;
        const matchRatio = wordMatches / queryWords.length;
        return averageScore * matchRatio;
      }
    }

    return 0;
  }

  /**
   * Check if a query matches any item in an array with support for partial matches
   */
  public hasMatch(
    query: string,
    candidates: string[],
    type: keyof typeof FUZZY_SEARCH_CONFIGS = 'general',
    minScore = 0.6
  ): boolean {
    const matches = this.findMatches(query, candidates, type);
    return matches.length > 0 && matches[0].score >= minScore;
  }

  /**
   * Get the best match from an array of candidates
   */
  public getBestMatch(
    query: string,
    candidates: string[],
    type: keyof typeof FUZZY_SEARCH_CONFIGS = 'general'
  ): MatchResult | null {
    const matches = this.findMatches(query, candidates, type);
    return matches.length > 0 ? matches[0] : null;
  }

  /**
   * Clear all cached Fuse instances (useful for memory management)
   */
  public clearCache(): void {
    this.fuseInstances.clear();
  }
}

/**
 * Result interface for enhanced matching
 */
export interface MatchResult {
  text: string;
  score: number;
  matchType: 'exact' | 'synonym' | 'fuzzy' | 'partial';
  originalIndex: number;
}

/**
 * Singleton instance for the enhanced fuzzy matcher
 */
export const fuzzyMatcher = new EnhancedFuzzyMatcher();

/**
 * Utility functions for common matching scenarios
 */

/**
 * Enhanced string similarity check with synonym support
 */
export const enhancedStringMatch = (
  str1: string,
  str2: string,
  type: keyof typeof FUZZY_SEARCH_CONFIGS = 'general',
  minScore = 0.6
): boolean => {
  const matches = fuzzyMatcher.findMatches(str1, [str2], type);
  return matches.length > 0 && matches[0].score >= minScore;
};

/**
 * Check if an array contains a term using enhanced matching
 */
export const enhancedArrayIncludes = (
  arr: string[],
  searchTerm: string,
  type: keyof typeof FUZZY_SEARCH_CONFIGS = 'general',
  minScore = 0.6
): boolean => {


  const result = fuzzyMatcher.hasMatch(searchTerm, arr, type, minScore);



  return result;
};

/**
 * Find all matching items in an array using enhanced matching
 */
export const enhancedArrayFilter = (
  arr: string[],
  searchTerm: string,
  type: keyof typeof FUZZY_SEARCH_CONFIGS = 'general',
  minScore = 0.6
): MatchResult[] => {
  return fuzzyMatcher.findMatches(searchTerm, arr, type).filter(match => match.score >= minScore);
};

/**
 * Enhanced text search in multiple fields
 */
export const enhancedMultiFieldSearch = (
  query: string,
  fields: { [key: string]: string | string[] | undefined },
  type: keyof typeof FUZZY_SEARCH_CONFIGS = 'general',
  minScore = 0.4
): { field: string; matches: MatchResult[] } => {


  const allMatches: { field: string; matches: MatchResult[] } = {
    field: '',
    matches: []
  };

  Object.entries(fields).forEach(([fieldName, fieldValue]) => {
    if (!fieldValue) return;

    let candidates: string[] = [];
    if (Array.isArray(fieldValue)) {
      candidates = fieldValue;
    } else {
      candidates = [fieldValue];
    }

    const matches = fuzzyMatcher.findMatches(query, candidates, type)
      .filter(match => match.score >= minScore);



    if (matches.length > 0 && matches[0].score > (allMatches.matches[0]?.score || 0)) {
      allMatches.field = fieldName;
      allMatches.matches = matches;
    }
  });



  return allMatches;
};

/**
 * Calculate a comprehensive similarity score between two objects
 */
export const calculateSimilarityScore = (
  filters: { [key: string]: string | string[] | undefined },
  cdmo: { [key: string]: string | string[] | undefined },
  fieldMappings: { [filterKey: string]: { cdmoField: string; type: keyof typeof FUZZY_SEARCH_CONFIGS; weight: number } }
): number => {
  let totalScore = 0;
  let maxPossibleScore = 0;

  Object.entries(fieldMappings).forEach(([filterKey, mapping]) => {
    const filterValue = filters[filterKey];
    const cdmoValue = cdmo[mapping.cdmoField];

    if (!filterValue) return;

    maxPossibleScore += mapping.weight;

    if (Array.isArray(filterValue)) {
      // Handle array filters
      let bestScore = 0;
      filterValue.forEach(item => {
        if (Array.isArray(cdmoValue)) {
          const matches = fuzzyMatcher.findMatches(item, cdmoValue, mapping.type);
          if (matches.length > 0) {
            bestScore = Math.max(bestScore, matches[0].score);
          }
        } else if (cdmoValue) {
          const matches = fuzzyMatcher.findMatches(item, [cdmoValue], mapping.type);
          if (matches.length > 0) {
            bestScore = Math.max(bestScore, matches[0].score);
          }
        }
      });
      totalScore += bestScore * mapping.weight;
    } else {
      // Handle single value filters
      if (Array.isArray(cdmoValue)) {
        const matches = fuzzyMatcher.findMatches(filterValue, cdmoValue, mapping.type);
        if (matches.length > 0) {
          totalScore += matches[0].score * mapping.weight;
        }
      } else if (cdmoValue) {
        const matches = fuzzyMatcher.findMatches(filterValue, [cdmoValue], mapping.type);
        if (matches.length > 0) {
          totalScore += matches[0].score * mapping.weight;
        }
      }
    }
  });

  return maxPossibleScore > 0 ? (totalScore / maxPossibleScore) : 0;
};

/**
 * CDMO data structure for search
 */
export interface CDMOData {
  company_name?: string;
  description?: string;
  facility_locations_details?: string;
  primary_capabilities?: string[];
  gmp_certifications?: string[];
  preferred_project_types?: string[];
  engagement_scales?: string[];
  typical_batch_sizes?: string[];
  target_client_types?: string[];
  budget_ranges?: string[];
  operating_regions?: string[];
  detailed_capabilities?: { [key: string]: string[] };
  [key: string]: string | string[] | { [key: string]: string[] } | undefined;
}

/**
 * Comprehensive search across all CDMO fields with detailed scoring
 */
export interface CDMOSearchResult {
  cdmo: CDMOData;
  totalScore: number;
  maxPossibleScore: number;
  percentageMatch: number;
  fieldMatches: { field: string; score: number; matchType: string; query: string }[];
}

export const comprehensiveSearchCDMOs = (
  query: string,
  cdmos: CDMOData[],
  options: {
    minScore?: number;
    maxResults?: number;
  } = {}
): CDMOSearchResult[] => {
  const { minScore = 0.2, maxResults = 10 } = options;

  if (!query || !cdmos.length) return [];

  const results: CDMOSearchResult[] = [];

  cdmos.forEach(cdmo => {
    let totalScore = 0;
    let maxPossibleScore = 0;
    const fieldMatches: { field: string; score: number; matchType: string; query: string }[] = [];

    // Define searchable fields with their weights and types
    const searchFields = [
      { field: 'company_name', weight: 3, type: 'companyName' as keyof typeof FUZZY_SEARCH_CONFIGS },
      { field: 'description', weight: 2, type: 'general' as keyof typeof FUZZY_SEARCH_CONFIGS },
      { field: 'facility_locations_details', weight: 1.5, type: 'general' as keyof typeof FUZZY_SEARCH_CONFIGS },
      { field: 'operating_regions', weight: 2, type: 'general' as keyof typeof FUZZY_SEARCH_CONFIGS },
      { field: 'primary_capabilities', weight: 2.5, type: 'capabilities' as keyof typeof FUZZY_SEARCH_CONFIGS },
      { field: 'gmp_certifications', weight: 2, type: 'certifications' as keyof typeof FUZZY_SEARCH_CONFIGS },
      { field: 'preferred_project_types', weight: 2, type: 'capabilities' as keyof typeof FUZZY_SEARCH_CONFIGS },
      { field: 'engagement_scales', weight: 1.5, type: 'capabilities' as keyof typeof FUZZY_SEARCH_CONFIGS },
      { field: 'typical_batch_sizes', weight: 1, type: 'capabilities' as keyof typeof FUZZY_SEARCH_CONFIGS },
      { field: 'target_client_types', weight: 1, type: 'capabilities' as keyof typeof FUZZY_SEARCH_CONFIGS },
      { field: 'budget_ranges', weight: 1.5, type: 'general' as keyof typeof FUZZY_SEARCH_CONFIGS }
    ];

    // Search each field
    searchFields.forEach(({ field, weight, type }) => {
      const fieldValue = cdmo[field];
      if (!fieldValue) return;

      maxPossibleScore += weight;

      let bestFieldScore = 0;
      let bestMatch: MatchResult | null = null;

      if (Array.isArray(fieldValue)) {
        // Search in array fields
        const matches = fuzzyMatcher.findMatches(query, fieldValue, type);
        if (matches.length > 0) {
          bestMatch = matches[0];
          bestFieldScore = bestMatch.score;
        }
      } else if (typeof fieldValue === 'string') {
        // Search in string fields
        const matches = fuzzyMatcher.findMatches(query, [fieldValue], type);
        if (matches.length > 0) {
          bestMatch = matches[0];
          bestFieldScore = bestMatch.score;
        }
      }

      // Check detailed_capabilities (nested object)
      if (field === 'primary_capabilities' && cdmo.detailed_capabilities) {
        Object.values(cdmo.detailed_capabilities).forEach((capabilities: string[]) => {
          if (Array.isArray(capabilities)) {
            const matches = fuzzyMatcher.findMatches(query, capabilities, type);
            if (matches.length > 0 && matches[0].score > bestFieldScore) {
              bestMatch = matches[0];
              bestFieldScore = matches[0].score;
            }
          }
        });
      }

      if (bestMatch && bestFieldScore > 0.2) {
        totalScore += bestFieldScore * weight;
        fieldMatches.push({
          field,
          score: bestFieldScore,
          matchType: bestMatch.matchType,
          query
        });
      }
    });

    const percentageMatch = maxPossibleScore > 0 ? Math.round((totalScore / maxPossibleScore) * 100) : 0;

    // Include result if it meets minimum criteria
    if (totalScore > 0 && percentageMatch >= (minScore * 100)) {
      results.push({
        cdmo,
        totalScore,
        maxPossibleScore,
        percentageMatch,
        fieldMatches
      });
    }
  });

  // Sort by percentage match (highest first)
  results.sort((a, b) => b.percentageMatch - a.percentageMatch);

  // Return top results
  return results.slice(0, maxResults);
};

/**
 * Quick partial search for autocomplete/suggestions
 */
export const quickPartialSearch = (
  query: string,
  candidates: string[],
  maxResults: number = 5
): MatchResult[] => {
  if (!query || query.length < 2) return [];

  const results = fuzzyMatcher.findMatches(query, candidates, 'partialSearch');

  // Include partial matches with lower scores
  const filteredResults = results.filter(result => result.score > 0.3);

  return filteredResults.slice(0, maxResults);
}; 